import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  NearestPropertiesBatchOptions,
  NearestPropertyRow,
  UseNearestPropertiesBatchResult,
  DistanceResult,
} from './types';
import { postProcessRows } from './postProcess';
import { createDistanceWorker } from './workerWrapper';

/**
 * Batch hook: computes nearest properties for multiple source extents
 * against the boundaries layer with a single query and a single worker dispatch.
 *
 * The caller provides source extents directly (e.g. from the acquisition layer),
 * so the hook never needs to look up source geometry by an ID field.
 *
 * Usage:
 * ```ts
 * const { resultsBySource, isLoading } = useNearestPropertiesBatch({
 *   sourceExtents,     // JSON-serialized extents from the acquisition layer
 *   sourceLabels,      // property titles or IDs for keying results
 *   boundariesLayer,
 *   topNPerType: 1,
 * });
 * ```
 */
export function useNearestPropertiesBatch(
  options: NearestPropertiesBatchOptions,
): UseNearestPropertiesBatchResult {
  const {
    sourceExtents,
    sourceLabels,
    boundariesLayer,
    limit,
    managementTypes,
    topNPerType,
    skip = false,
  } = options;

  const [rawBatchResults, setRawBatchResults] = useState<{
    resultsBySourceIndex: DistanceResult[][];
  } | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Stable key from labels to avoid re-running on every render
  const labelsKey = useMemo(() => sourceLabels.map(String).sort().join(','), [sourceLabels]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (skip || !sourceExtents.length || !boundariesLayer) {
      setRawBatchResults(null);
      setIsLoading(false);
      setError(undefined);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(undefined);

      try {
        // 1. Query boundaries layer once for all target features
        const allFeatures = await boundariesLayer.queryFeatures({
          returnGeometry: true,
          outFields: ['*'],
        });
        if (cancelled) return;

        const featuresJSON = allFeatures.toJSON().features;

        // 2. Single worker dispatch — source extents are already provided
        const worker = createDistanceWorker();

        const batchResults = await worker.calculateDistancesBatch(featuresJSON, sourceExtents);

        if (cancelled || currentRequestId !== requestIdRef.current) return;

        setRawBatchResults({ resultsBySourceIndex: batchResults });
      } catch (err) {
        if (!cancelled && currentRequestId === requestIdRef.current) {
          setError(err instanceof Error ? err.message : 'Batch distance computation failed');
        }
      } finally {
        if (!cancelled && currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [labelsKey, sourceExtents, boundariesLayer, skip]);

  // Post-process each source's results
  const resultsBySource = useMemo<Record<string, NearestPropertyRow[]>>(() => {
    if (!rawBatchResults) return {};

    const { resultsBySourceIndex } = rawBatchResults;
    const output: Record<string, NearestPropertyRow[]> = {};

    for (let i = 0; i < sourceLabels.length; i++) {
      const label = sourceLabels[i];
      const rawResults = resultsBySourceIndex[i] ?? [];

      output[String(label)] = postProcessRows(rawResults, {
        managementTypes,
        topNPerType,
        limit,
      });
    }

    return output;
  }, [rawBatchResults, sourceLabels, managementTypes, topNPerType, limit]);

  return {
    resultsBySource,
    unresolvedIds: [],
    isLoading,
    error,
  };
}
